/**
 * Casting-lot aggregates for the dashboard: what the Defect Pareto, the scrap KPI and the scrap trend read on a plant
 * that books its scrap on casting lots (an imported CMS), where its heats carry no outcome of their own. The same rollup
 * the lot reports read (`GET /v1/twin/lots/rollup`, pieces summed server-side), so a board's numbers reconcile with the
 * Reports page. The window is the widget's pinned data window, else its lookback.
 *
 * The shape is the heats' (`TwinAggregates`), so the widgets pick one source or the other and read it the same way:
 * `lotsAnswer` decides, as the reports decide (lib/reports/lot-report `reportsFromLots`): lots when the plant's heats are
 * all imported, or there are none, and lots were booked in the window.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { isLiveApi } from "@/lib/api";
import { twinLotRollup, type LotRollup } from "@/lib/api/twin-lot-rollup";
import type { TwinAggregates } from "./twin-aggregates";
import { DEFAULT_RANGE, rangeLookbackDays, type RangeToken, type WidgetWindow } from "./use-live-tag-data";

export interface LotAggregates extends TwinAggregates {
  /** Casting lots booked in the window (0: the plant booked none there). */
  lots: number;
}

/** The most defect bars a Pareto shows. */
const PARETO_BARS = 12;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * A rollup by day as the dashboard's aggregates. The Pareto counts scrapped PIECES per defect as booked, most first; the
 * trend is each day's scrap percentage, oldest first; the KPI roll-ups are the window's totals, with `heats` holding the
 * lots booked. Nothing cast means no rate (null), never a zero. Pure.
 */
export function lotAggregatesOf(r: LotRollup | undefined): Omit<LotAggregates, "isLoading"> {
  const t = r?.totals;
  const cast = t?.cast ?? 0;
  const scrap = t?.scrap ?? 0;
  const lots = t?.lots ?? 0;
  const defectPareto = Object.entries(t?.defects ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, PARETO_BARS)
    .map(([name, count]) => ({ name: name.replace(/_/g, " "), count }));
  const dailyScrapTrend = [...(r?.rows ?? [])]
    .filter((row) => row.cast > 0)
    .map((row) => ({ t: row.first_day ?? row.key, v: round2((row.scrap / row.cast) * 100) }))
    .sort((a, b) => a.t.localeCompare(b.t));
  // Nothing cast is no rate (null), never a zero: each rate is its own statement.
  const yieldPct = cast > 0 ? ((cast - scrap) / cast) * 100 : null;
  const rejectionPct = cast > 0 ? (scrap / cast) * 100 : null;
  return {
    defectPareto,
    dailyScrapTrend,
    kpi: {
      yieldPct,
      rejectionPct,
      partsProduced: cast,
      partsAccepted: cast - scrap,
      partsRejected: scrap,
      heats: lots,
      // Lots carry no pass or fail, and no KPI reads this (resolveTwinKpi): it only fills the shape.
      failedHeats: 0,
    },
    heatCount: lots,
    allHeatsImported: false,
    lots,
  };
}

/** Whether a board reads the casting lots rather than the heats (see the file comment). Pure. */
export function lotsAnswer(heats: Pick<TwinAggregates, "heatCount" | "allHeatsImported">, lots: number): boolean {
  return lots > 0 && (heats.heatCount === 0 || heats.allHeatsImported);
}

/**
 * The daily scrap trend a board plots: the casting lots' where the plant books its scrap on them (`lotsAnswer`), else
 * its heats'. The Timeseries widget's scrap line reads it, as the KPI and the Pareto read their roll-ups. Pure.
 */
export function scrapTrendOf(
  heats: Pick<TwinAggregates, "heatCount" | "allHeatsImported" | "dailyScrapTrend">,
  lots: Pick<LotAggregates, "lots" | "dailyScrapTrend">,
): TwinAggregates["dailyScrapTrend"] {
  return lotsAnswer(heats, lots.lots) ? lots.dailyScrapTrend : heats.dailyScrapTrend;
}

/**
 * The lots booked in a widget's window, aggregated. `enabled` is the caller's say (only a plant whose heats are all
 * imported, or which has none, reads its lots); the query is live-only.
 */
export function useLotAggregates(dataWindow: WidgetWindow | undefined, range: RangeToken | undefined, enabled: boolean): LotAggregates {
  const token = range ?? DEFAULT_RANGE;
  const q = useQuery({
    queryKey: ["dashboard-lot-rollup", dataWindow?.from ?? "", dataWindow?.to ?? "", dataWindow ? "" : token],
    enabled: isLiveApi && enabled,
    staleTime: 60_000,
    queryFn: () => {
      if (dataWindow) return twinLotRollup.get({ by: "day", from: dataWindow.from, to: dataWindow.to });
      // A lookback ends today: Date.now() lives here, in the query, never in a render.
      const from = new Date(Date.now() - rangeLookbackDays(token) * 86_400_000).toISOString().slice(0, 10);
      return twinLotRollup.get({ by: "day", from });
    },
  });
  return useMemo(() => ({ ...lotAggregatesOf(q.data), isLoading: isLiveApi && enabled && q.isLoading }), [q.data, q.isLoading, enabled]);
}
